import { useEffect, useRef, useState } from "react";
import { Button, Form, InputGroup, OverlayTrigger, Popover } from "react-bootstrap";

export default function AddTierPopover({ disabled, minCutDay, onConfirm, container }) {
    const [show, setShow] = useState(false);
    const [cutDay, setCutDay] = useState("");
    const [error, setError] = useState("");
    const inputRef = useRef(null);

    const focusCaretToEnd = () => {
        const el = inputRef.current;
        if (!el) return;
        el.focus();
        const len = el.value?.length ?? 0;
        try {
            el.setSelectionRange(len, len);
        } catch {
            // ignore
        }
    };

    useEffect(() => {
        if (!show) return;
        setError("");
        setCutDay(String(minCutDay));
        setTimeout(focusCaretToEnd, 0);
    }, [show, minCutDay]);

    const cut = Number(cutDay);
    const isValidCut = Number.isFinite(cut) && cut >= minCutDay;

    const doAdd = () => {
        if (!isValidCut) {
            setError(`Please enter a number ≥ ${minCutDay}.`);
            setTimeout(focusCaretToEnd, 0);
            return;
        }
        onConfirm(cut);
        setError("");
        setCutDay(String(cut + 1));
        setTimeout(focusCaretToEnd, 0);
    };

    const exampleEnd = isValidCut ? cut : minCutDay;
    const exampleNext = exampleEnd + 1;
    const inCustomContainer = !!container;

    return (
        <OverlayTrigger
            trigger="click"
            placement="bottom-start"
            show={show}
            onToggle={(next) => setShow(next)}
            rootClose={!inCustomContainer}
            container={container || undefined}
            popperConfig={{
                strategy: "absolute",
                modifiers: [
                    {
                        name: "offset",
                        options: {
                            offset: [0, 8],
                        },
                    },
                    {
                        name: "preventOverflow",
                        options: {
                            boundary: container || "viewport",
                            altBoundary: true,
                            tether: true,
                            padding: 8,
                        },
                    },
                    {
                        name: "flip",
                        options: {
                            fallbackPlacements: ["top-start"],
                        },
                    },
                ],
            }}
            overlay={
                <Popover
                    id="popover-add-tier"
                    style={{
                        width: 320,
                        maxWidth: inCustomContainer ? "min(320px, calc(100% - 16px))" : "calc(100vw - 24px)",
                        zIndex: 1065,
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <Popover.Header as="div" className="fw-semibold">
                        Add price tier
                    </Popover.Header>
                    <Popover.Body>
                        <div className="text-muted mb-3" style={{ fontSize: 13, lineHeight: 1.4 }}>
                            Set the last day for this price. We’ll create a new tier for longer rentals.
                        </div>

                        <Form.Label className="fw-semibold" style={{ fontSize: 13 }}>
                            This price applies up to
                        </Form.Label>

                        <InputGroup className="mb-2">
                            <Form.Control
                                ref={inputRef}
                                type="text"
                                inputMode="numeric"
                                value={cutDay}
                                onChange={(e) => {
                                    setCutDay(e.target.value.replace(/[^\d]/g, ""));
                                    setError("");
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Escape") {
                                        e.preventDefault();
                                        setShow(false);
                                    } else if (e.key === "Enter") {
                                        e.preventDefault();
                                        doAdd();
                                    }
                                }}
                            />
                            <InputGroup.Text>days</InputGroup.Text>
                        </InputGroup>

                        {error ? (
                            <div className="text-danger mb-2" style={{ fontSize: 13 }}>
                                {error}
                            </div>
                        ) : (
                            <div className="text-muted mb-3" style={{ fontSize: 12 }}>
                                Example: Days 1–{exampleEnd}, Days {exampleNext}+
                            </div>
                        )}

                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="outline-secondary" size="sm" onClick={() => setShow(false)}>
                                Done
                            </Button>
                            <Button variant="primary" size="sm" onClick={doAdd} disabled={!isValidCut}>
                                Add tier
                            </Button>
                        </div>
                    </Popover.Body>
                </Popover>
            }
        >
            <span>
                <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={disabled}
                    onClick={() => setShow((s) => !s)}
                >
                    + Add tier
                </Button>
            </span>
        </OverlayTrigger>
    );
}